import type {
  HsDecodedGeometryList,
  HsGeometryDecodeKind,
  HsGeometryDecodeRequest,
  HsGeometryDecodeResponse,
} from '@sibe/hyperscene/workers/geometry-decode';

/** Message of the error a request rejects with when its scene epoch is over or the pool is disposed. */
export const HS_GEOMETRY_DECODE_CANCELED: string = 'DECODE_CANCELED';

export interface HsGeometryDecodePoolOptions {
  /** Creates one worker that runs `hsHandleGeometryDecodeMessage`; hosts build it with their bundler. */
  createWorker: () => Worker;
  /** The scene epoch now; results of requests made in an older epoch are discarded as canceled. */
  currentEpoch: () => number;
  onInflightChange?: (inflight: number) => void;
  onCanceled?: (count: number) => void;
  /** A worker crashed; every pending request was rejected and the pool emptied. */
  onWorkerFailure?: () => void;
}

interface PendingDecode {
  epoch: number;
  resolve: (value: HsDecodedGeometryList) => void;
  reject: (reason: Error) => void;
}

/** Round-robin pool of geometry decode workers with scene-epoch cancellation. */
export class HsGeometryDecodePool {
  private workers: Worker[] = [];
  private readonly pending: Map<number, PendingDecode> = new Map();
  private nextRequestId: number = 1;
  private cursor: number = 0;

  public constructor(private readonly options: HsGeometryDecodePoolOptions) {}

  public get size(): number {
    return this.workers.length;
  }

  /** Creates or replaces workers so the pool has `size` of them (clamped to 1-8). */
  public ensure(size: number): void {
    if (typeof Worker === 'undefined') {
      this.workers = [];
      return;
    }
    const target: number = Math.max(1, Math.min(8, size));
    if (this.workers.length === target) {
      return;
    }
    this.dispose();
    for (let index: number = 0; index < target; index++) {
      const worker: Worker = this.options.createWorker();
      worker.onmessage = (event: MessageEvent<HsGeometryDecodeResponse>) => this.settle(event.data);
      worker.onerror = () => this.fail(new Error('Geometry decode worker failed.'));
      this.workers.push(worker);
    }
    this.cursor = 0;
  }

  /** Decodes one payload in the next worker; the payload is copied, so the caller keeps its buffer. */
  public decode<T>(kind: HsGeometryDecodeKind, payload: ArrayBuffer, epoch: number): Promise<T[]> {
    if (this.workers.length === 0) {
      return Promise.reject(new Error('Decode worker pool is empty.'));
    }
    if (epoch !== this.options.currentEpoch()) {
      return Promise.reject(new Error(HS_GEOMETRY_DECODE_CANCELED));
    }
    const worker: Worker = this.workers[this.cursor++ % this.workers.length];
    const requestId: number = this.nextRequestId++;
    const result: Promise<HsDecodedGeometryList> = new Promise<HsDecodedGeometryList>(
      (resolve: (value: HsDecodedGeometryList) => void, reject: (reason: Error) => void) => {
        this.pending.set(requestId, { epoch, resolve, reject });
        this.options.onInflightChange?.(this.pending.size);
      }
    );
    const copy: ArrayBuffer = payload.slice(0);
    const request: HsGeometryDecodeRequest = { requestId, kind, payload: copy };
    worker.postMessage(request, [copy]);
    return result as Promise<unknown> as Promise<T[]>;
  }

  /** Rejects pending requests as canceled; the workers keep running and their late results are dropped. */
  public cancelPending(): void {
    if (this.pending.size > 0) {
      this.options.onCanceled?.(this.pending.size);
    }
    this.pending.forEach((entry: PendingDecode) => entry.reject(new Error(HS_GEOMETRY_DECODE_CANCELED)));
    this.pending.clear();
    this.options.onInflightChange?.(0);
  }

  /** Terminates the workers and rejects pending requests as canceled. */
  public dispose(): void {
    this.workers.forEach((worker: Worker) => worker.terminate());
    this.workers = [];
    this.cancelPending();
  }

  private settle(response: HsGeometryDecodeResponse): void {
    const entry: PendingDecode | undefined = this.pending.get(response.requestId);
    if (entry === undefined) {
      return;
    }
    this.pending.delete(response.requestId);
    this.options.onInflightChange?.(this.pending.size);
    if (entry.epoch !== this.options.currentEpoch()) {
      this.options.onCanceled?.(1);
      entry.reject(new Error(HS_GEOMETRY_DECODE_CANCELED));
      return;
    }
    if (response.ok) {
      entry.resolve(response.result);
    } else {
      entry.reject(new Error(response.error));
    }
  }

  private fail(error: Error): void {
    this.options.onWorkerFailure?.();
    this.pending.forEach((entry: PendingDecode) => entry.reject(error));
    this.pending.clear();
    this.options.onInflightChange?.(0);
    this.workers.forEach((worker: Worker) => worker.terminate());
    this.workers = [];
  }
}
